import { useEffect, useRef } from "react";

import { useRuntimeContext } from "@/features/tutor/context/RuntimeContext";
import { useChatSettingsStore } from "@/features/tutor/store/useChatSettingsStore";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";
import { useWebsocketStore } from "@/features/tutor/store/useWebsocketStore";

export const useTutorAudioReporter = (): void => {
  const { emit } = useRuntimeContext() ?? {};
  const connected = useWebsocketStore((s) => s.wsConnected);
  const effectiveTutorAudioOn = useChatSettingsStore(
    (s) => s.effectiveTutorAudioOn,
  );
  const reported = useRef<boolean | null>(null);

  useEffect(
    () =>
      useTurnStore.subscribe((state, previous) => {
        if (state.turnId === previous.turnId) return;
        // dispatchTutorTurn()/startTutorTurn() already apply the pending
        // mode at the moment a turn is admitted, before its id is even
        // known — apply_cached_bite_choice and the RESET restart both set
        // turnId only once the request resolves. Re-applying here for that
        // same turn would flip the frontend's mode away from what was
        // actually sent to the backend, mid-flight. A turnId change is only
        // a fresh boundary worth flushing pending for when no dispatch is
        // currently in force — canvas turnId bookkeeping and any turn that
        // never goes through dispatchTutorTurn are what this still covers.
        if (useChatSettingsStore.getState().turnDispatched) return;
        useChatSettingsStore.getState().applyPendingTutorAudio();
      }),
    [],
  );

  useEffect(() => {
    if (!connected || !emit) {
      reported.current = null;
      return;
    }
    if (reported.current === effectiveTutorAudioOn) return;

    reported.current = effectiveTutorAudioOn;
    emit("TUTOR_AUDIO_CHANGED", effectiveTutorAudioOn);
  }, [connected, emit, effectiveTutorAudioOn]);
};
